/**
 * Seated event conflict detection (overlap, missing layout seat, hold/ticket mismatch).
 */

export const getTicketSeats = (ticket) => {
  if (!ticket || typeof ticket !== 'object') return [];
  if (Array.isArray(ticket.seats)) return ticket.seats;
  if (Array.isArray(ticket.extras?.seats)) return ticket.extras.seats;
  return [];
};

export const collectLayoutSeatMeta = (layoutJSON) => {
  const byLabel = new Map();
  if (!layoutJSON || typeof layoutJSON !== 'object') return byLabel;

  const walk = (node) => {
    if (!node || typeof node !== 'object') return;
    if (node.className === 'Group' && Array.isArray(node.children)) {
      const textNode = node.children.find((c) => c.className === 'Text');
      const label = textNode?.attrs?.text != null ? String(textNode.attrs.text) : null;
      if (label) {
        byLabel.set(label, {
          label,
          productId: node.attrs?.ticket || null,
          groupId: node.attrs?.id || null,
        });
      }
    }
    if (Array.isArray(node.children)) {
      node.children.forEach(walk);
    }
  };

  walk(layoutJSON);
  return byLabel;
};

/** Flatten seatSelections body: { [seatLabel]: { status, user, ... } } */
export const normalizeSeatSelections = (raw) => {
  if (!raw || typeof raw !== 'object') return {};
  const { id, createdate, lastupdate, createdby, updatedby, body, ...rest } = raw;
  if (body && typeof body === 'object' && !Array.isArray(body)) {
    return { ...body };
  }
  return { ...rest };
};

const isSeatKey = (k) => /^[A-Za-z]/.test(String(k || ''));

const statusLabel = (s) => {
  if (s === 1) return '1 (satılmış/kilit)';
  if (s === 2) return '2 (hold)';
  if (s === 0) return '0 (boş)';
  if (s == null || Number.isNaN(s)) return 'yok';
  return String(s);
};

/**
 * 1. yer (Postgres / Firebase seatSelections) vs 2. yer (stocks.seatSelections).
 * seatsByTicket: bilet var mi yazmak icin.
 */
export function compareSeatSelectionSources(
  primaryRaw,
  stockFirebaseRaw,
  seatsByTicket = null,
) {
  const primary = normalizeSeatSelections(primaryRaw);
  const secondary = normalizeSeatSelections(stockFirebaseRaw);
  const conflicts = [];
  const mismatchSeatIds = [];

  const ticketNote = (seatId) => {
    const list = seatsByTicket?.get?.(seatId) || [];
    if (!list.length) return 'Bilet: yok';
    const pnrs = list.map((t) => t.pnr || t.id).join(', ');
    return `Bilet: var (${list.length}) · ${pnrs}`;
  };

  const keys = new Set([
    ...Object.keys(primary).filter(isSeatKey),
    ...Object.keys(secondary).filter(isSeatKey),
  ]);

  keys.forEach((seatId) => {
    const a = primary[seatId];
    const b = secondary[seatId];
    const statusA = a && typeof a === 'object' ? Number(a.status) : null;
    const statusB = b && typeof b === 'object' ? Number(b.status) : null;
    const ticketList = seatsByTicket?.get?.(seatId) || [];
    const hasTicket = ticketList.length > 0;
    const ticketsLine = ticketNote(seatId);

    // Hold / sepet gurultusu gosterme — sadece bileti olan koltuklar
    if (!hasTicket) return;

    // Hold (2) senkronlari gosterme; sadece 1 (kilit/satilmis) ile ilgili farklar
    const involvesSold =
      statusA === 1 || statusB === 1;
    if (!involvesSold) return;

    if ((statusA == null || Number.isNaN(statusA)) && (statusB == null || Number.isNaN(statusB))) {
      return;
    }

    // 1. yer var, stocks yok
    if (statusA != null && !Number.isNaN(statusA) && (b == null || statusB == null || Number.isNaN(statusB))) {
      conflicts.push({
        type: 'stock_fb_missing',
        severity: 'error',
        seatId,
        message:
          `${seatId}: seatSelections=${statusLabel(statusA)} yazılmış ama stocks.seatSelections YAZILMAMIŞ. ${ticketsLine}`,
      });
      mismatchSeatIds.push(seatId);
      return;
    }

    // stocks var, 1. yer yok
    if (statusB != null && !Number.isNaN(statusB) && (a == null || statusA == null || Number.isNaN(statusA))) {
      conflicts.push({
        type: 'stock_fb_orphan',
        severity: 'warning',
        seatId,
        message:
          `${seatId}: stocks.seatSelections=${statusLabel(statusB)} var ama seatSelections kaydı YOK. ${ticketsLine}`,
      });
      mismatchSeatIds.push(seatId);
      return;
    }

    // Status farkli (orn. seatSelections=1, stocks=2 hold)
    if (statusA !== statusB) {
      conflicts.push({
        type: 'stock_fb_mismatch',
        severity: 'error',
        seatId,
        message:
          `${seatId}: seatSelections=${statusLabel(statusA)} ama stocks.seatSelections=${statusLabel(statusB)} (2. yer farklı). ${ticketsLine}`,
      });
      mismatchSeatIds.push(seatId);
    }
  });

  return { conflicts, mismatchSeatIds: [...new Set(mismatchSeatIds)] };
}

/**
 * @returns {{
 *   conflicts: Array<{ type: string, severity: 'error'|'warning', seatId: string, message: string, ticketIds?: string[] }>,
 *   soldSeatIds: string[],
 *   holdSeatIds: string[],
 *   conflictSeatIds: string[],
 *   seatsByTicket: Map<string, object[]>,
 *   stockFbMismatchSeatIds?: string[],
 * }}
 */
export function analyzeSeatConflicts({
  tickets = [],
  layoutJSON = null,
  seatSelections = null,
  stockFirebaseSeatSelections = null,
} = {}) {
  const layoutSeats = collectLayoutSeatMeta(layoutJSON);
  const selections = normalizeSeatSelections(seatSelections);
  const seatsByTicket = new Map(); // seatId -> tickets[]
  const conflicts = [];

  (tickets || []).forEach((ticket) => {
    getTicketSeats(ticket).forEach((seat) => {
      const seatId = seat?.id != null ? String(seat.id) : null;
      if (!seatId) return;
      if (!seatsByTicket.has(seatId)) seatsByTicket.set(seatId, []);
      seatsByTicket.get(seatId).push(ticket);
    });
  });

  // 1) Same seat on multiple tickets
  seatsByTicket.forEach((list, seatId) => {
    if (list.length > 1) {
      conflicts.push({
        type: 'overlap',
        severity: 'error',
        seatId,
        ticketIds: list.map((t) => t.id),
        message: `Koltuk ${seatId}: ${list.length} bilette çakışıyor (${list
          .map((t) => t.pnr || t.id)
          .join(', ')})`,
      });
    }
  });

  // 2) Ticket seat missing from layout
  seatsByTicket.forEach((list, seatId) => {
    if (!layoutSeats.has(seatId)) {
      conflicts.push({
        type: 'missing_layout',
        severity: 'error',
        seatId,
        ticketIds: list.map((t) => t.id),
        message: `Koltuk ${seatId}: layout'ta yok ama bilet(ler)de var`,
      });
    }
  });

  // 3) Product mismatch (ticket product / seat.productID vs layout attrs.ticket)
  seatsByTicket.forEach((list, seatId) => {
    const meta = layoutSeats.get(seatId);
    if (!meta?.productId) return;
    list.forEach((ticket) => {
      const seatRow = getTicketSeats(ticket).find((s) => String(s?.id) === String(seatId));
      const productId =
        seatRow?.productID ||
        ticket.product?.id ||
        ticket.extras?.product?.id;
      if (productId && String(productId) !== String(meta.productId)) {
        conflicts.push({
          type: 'product_mismatch',
          severity: 'warning',
          seatId,
          ticketIds: [ticket.id],
          message: `Koltuk ${seatId}: bilet ürünü (${productId}) layout ürününden (${meta.productId}) farklı`,
        });
      }
    });
  });

  // 4) seatSelections sold/hold vs tickets
  Object.entries(selections).forEach(([seatId, sel]) => {
    if (!sel || typeof sel !== 'object') return;
    const status = Number(sel.status);
    const ticketList = seatsByTicket.get(seatId) || [];

    if (status === 1 && ticketList.length === 0) {
      if (sel.isBlockedSeat === true) {
        return;
      }
      conflicts.push({
        type: 'sold_without_ticket',
        severity: 'warning',
        seatId,
        message: `Koltuk ${seatId}: seatSelections satılmış (1) ama bilet kaydı yok`,
      });
    }
  });

  seatsByTicket.forEach((ticketList, seatId) => {
    const sel = selections[seatId];
    const status = sel && typeof sel === 'object' ? Number(sel.status) : null;

    if (status === 1) return;

    if (status == null || Number.isNaN(status) || !sel) {
      conflicts.push({
        type: 'ticket_without_sold_flag',
        severity: 'warning',
        seatId,
        ticketIds: ticketList.map((t) => t.id),
        message: `Koltuk ${seatId}: bilet var ama seatSelections kaydı yok — panelde satılmış flag atanmamış`,
      });
      return;
    }

    if (status === 0) {
      conflicts.push({
        type: 'ticket_without_sold_flag',
        severity: 'warning',
        seatId,
        ticketIds: ticketList.map((t) => t.id),
        message: `Koltuk ${seatId}: bilet var ama seatSelections boş (0)`,
      });
      return;
    }

    if (status === 2) {
      conflicts.push({
        type: 'ticket_without_sold_flag',
        severity: 'warning',
        seatId,
        ticketIds: ticketList.map((t) => t.id),
        message: `Koltuk ${seatId}: bilet var ama seatSelections hâlâ hold (2)`,
      });
    }
  });

  // 5) 2. yer: Firebase stocks.seatSelections vs Postgres
  let stockFbMismatchSeatIds = [];
  if (stockFirebaseSeatSelections != null) {
    const sync = compareSeatSelectionSources(
      selections,
      stockFirebaseSeatSelections,
      seatsByTicket,
    );
    conflicts.push(...sync.conflicts);
    stockFbMismatchSeatIds = sync.mismatchSeatIds;
  }

  const blockedSeatIds = new Set();
  Object.entries(selections).forEach(([seatId, sel]) => {
    if (sel && typeof sel === 'object' && sel.isBlockedSeat === true) {
      blockedSeatIds.add(seatId);
    }
  });
  // Canli web (Firebase stocks.seatSelections) admin rezerv — PG geride kalsa bile ekranda gorunsun
  // Biletli koltuklar da listede kalsin (admin rezerv + satis cakismasi gorunsun)
  if (stockFirebaseSeatSelections != null) {
    const fbSel = normalizeSeatSelections(stockFirebaseSeatSelections);
    Object.entries(fbSel).forEach(([seatId, sel]) => {
      if (sel && typeof sel === 'object' && sel.isBlockedSeat === true) {
        blockedSeatIds.add(seatId);
      }
    });
  }

  const soldSeatIds = new Set();
  seatsByTicket.forEach((_, seatId) => soldSeatIds.add(seatId));
  Object.entries(selections).forEach(([seatId, sel]) => {
    if (Number(sel?.status) === 1 && sel?.isBlockedSeat !== true && !blockedSeatIds.has(seatId)) {
      soldSeatIds.add(seatId);
    }
  });

  const holdSeatIds = new Set();
  Object.entries(selections).forEach(([seatId, sel]) => {
    if (
      Number(sel?.status) === 2 &&
      !soldSeatIds.has(seatId) &&
      !blockedSeatIds.has(seatId)
    ) {
      holdSeatIds.add(seatId);
    }
  });

  const OVERLAP_TYPES = new Set(['overlap', 'missing_layout']);

  const conflictSeatIds = [
    ...new Set(
      conflicts
        .filter((c) => OVERLAP_TYPES.has(c.type))
        .map((c) => c.seatId),
    ),
  ];

  return {
    conflicts,
    soldSeatIds: [...soldSeatIds],
    holdSeatIds: [...holdSeatIds],
    blockedSeatIds: [...blockedSeatIds],
    conflictSeatIds,
    seatsByTicket,
    stockFbMismatchSeatIds,
  };
}
